/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import * as mobx from 'mobx';
import { types, getEnv } from 'mobx-state-tree';

export const WIDGET_EVENTS = {
	THUMBNAIL_GENERATION: {
		SUCCESS: 'thumbnail:generation:success',
		ERROR: 'thumbnail:generation:error'
	}
};

export default types
	.model('BackgroundThumbnails', {
		queued: types.optional(types.array(types.string), []),
		current: types.optional(types.array(types.string), []),
		done: types.optional(types.array(types.string), [])
	})
	.volatile(() => ({
		_mobxDisposeEvents: []
	}))
	.views((self) => ({

		/**
		 * Check whether the card is in `queued` state
		 *
		 * @param {string} cardId - card id to check
		 * @return {Boolean} TRUE if card is in the `current` state and FALSE if not
		 */
		isQueued(cardId) {
			return self.queued.indexOf(cardId) !== -1;
		},

		/**
		 * Check whether the card is in `current` state
		 *
		 * @param {string} cardId - card id to check
		 * @return {Boolean} TRUE if card is in the `current` state and FALSE if not
		 */
		isCurrent(cardId) {
			return self.current.indexOf(cardId) !== -1;
		},

		/**
		 * Check whether the card is in `done` state
		 *
		 * @param {string} cardId - card id to check
		 * @return {Boolean} TRUE if card is in the `done` state and FALSE if not
		 */
		isDone(cardId) {
			return self.done.indexOf(cardId) !== -1;
		},

		/**
		 * Length of the `current` list
		 *
		 * @return {number}
		 */
		get currentLength() {
			const currentLength = getEnv(self).currentLength;
			return currentLength === undefined ? 1 : currentLength;
		},

		/**
		 * Computed property to get the next callback that controls which
		 * item to pick from the `queued` pool of card ids and goes to the
		 * `current` pool
		 *
		 * Note:
		 * To override add `nextFn` to the model env
		 *
		 * @return {Function}
		 */
		get next() {
			const nextFn = getEnv(self).nextFn || ((queued) => {
				if (queued.length > 0) {
					// by default, always get the first
					return queued[0];
				}
			});
			return nextFn(self.queued);
		},

		/**
		 * Computed property to get all the card ids from all the lists
		 * @return {[type]} [description]
		 */
		get all() {
			return [self.queued, self.current, self.done]
				.reduce((acc, current) => {
					acc.push(...current);
					return acc;
				}, []);
		}
	}))
	.actions((self) => {

		/**
		 * Transfer card ids from the `queued` to `current` list when needed
		 */
		const syncNextCurrent = () => {
			if (self.current.length < self.currentLength) {
				const diff = self.currentLength - self.current.length;
				for (let i = 0; i < diff; i++) {
					const nextCurrent = self.next;
					if (nextCurrent) {
						self.removeFromQueued(nextCurrent);
						self.addToCurrent(nextCurrent);
					}
				}
			}
		};

		return {

			/**
			 * After create lifecycle handler
			 */
			afterCreate() {
				self._mobxDisposeEvents.push(

					mobx.reaction(() => {
						return self.queued.length;
					}, () => {
						syncNextCurrent();
					}),

					mobx.reaction(() => {
						return self.current.length;
					}, () => {
						syncNextCurrent();
					}),
				);

				if (self.queued.length > 0) {
					syncNextCurrent();
				}
			},

			/**
			 * Before destroy lifecycle handler
			 *
			 * Takes care of:
			 * - disposing pending event listeners
			 * -- disposing of pending mobx reactions
			 */
			beforeDestroy() {
				self._mobxDisposeEvents.forEach(dispose => dispose());
			},

			resetQueue() {
				self.queued = [];
			},

			/**
			 * Queue card ids for background thumbnail generation
			 *
			 * @param {string[]} cardIds - card ids to generate thumbnail in the background
			 */
			queue(cardIds) {
				if (!cardIds || cardIds.length === 0) {
					return;
				}

				const newQueued = [];
				cardIds.forEach(cardId => {
					const alreadyCurrent = self.current.indexOf(cardId) !== -1;
					const alreadyDone = self.done.indexOf(cardId) !== -1;

					if (!alreadyCurrent && !alreadyDone) {
						newQueued.push(cardId);
					}
				});

				self.queued = newQueued;
			},

			removeFromQueued(cardId) {
				const cardIds = Array.isArray(cardId) ? cardId : [cardId];
				const newQueued = [];
				self.queued.forEach(id => {
					if (cardIds.indexOf(id) === -1) {
						newQueued.push(id);
					}
				});
				self.queued = newQueued;
			},

			removeFromCurrent(cardId) {
				const cardIds = Array.isArray(cardId) ? cardId : [cardId];
				const newCurrent = [];
				self.current.forEach(id => {
					if (cardIds.indexOf(id) === -1) {
						newCurrent.push(id);
					}
				});
				self.current = newCurrent;
			},

			removeFromDone(cardId) {
				const cardIds = Array.isArray(cardId) ? cardId : [cardId];
				const newDone = [];
				self.done.forEach(id => {
					if (cardIds.indexOf(id) === -1) {
						newDone.push(id);
					}
				});
				self.done = newDone;
			},

			removeFromAll(cardId) {
				const cardIds = Array.isArray(cardId) ? cardId : [cardId];
				cardIds.forEach(id => {
					if (self.queued.indexOf(id) !== -1) {
						self.removeFromQueued(id);
					}

					if (self.current.indexOf(id) !== -1) {
						self.removeFromCurrent(id);
					}

					if (self.done.indexOf(id) !== -1) {
						self.removeFromDone(id);
					}
				});
			},

			addToCurrent(cardId) {
				const cardIds = Array.isArray(cardId) ? cardId : [cardId];
				self.current.push.apply(self.current, cardIds);
			},

			addToDone(cardId) {
				const cardIds = Array.isArray(cardId) ? cardId : [cardId];
				self.done.push.apply(self.done, cardIds);
			},

			renderCard(cardId) {
				const cardLayout = getEnv(self).dashboardApi.getCanvas().getLayout(cardId);
				const widgetLayouts = cardLayout.getChildren();

				widgetLayouts.forEach(widgetLayout => {
					widgetLayout.render({
						isBackgroundRun: true
					});
				});
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/layout/explorecontainer/thumbnail/BackgroundThumbnailsModel.js